import { CLARIFICATION_CANCEL_REASON, CLARIFICATION_COPY, CLARIFICATION_RESOLUTION_MODE, CLARIFICATION_STATE } from './constants';
import {
    ClarificationAnswer,
    ClarificationCancelReason,
    ClarificationCard,
    ClarificationResolution,
    ClarificationResolutionQuestion,
    ClarificationState,
} from './clarificationTypes';

const CANCEL_REASON_COPY: Record<ClarificationCancelReason, string> = {
    [CLARIFICATION_CANCEL_REASON.STOP]: CLARIFICATION_COPY.CANCEL_STOP,
    [CLARIFICATION_CANCEL_REASON.NEW_TURN]: CLARIFICATION_COPY.CANCEL_NEW_TURN,
};

/** One line of the read-only Q&A note. `answer` is null when nothing was answered. */
export interface ClarificationNoteLine {
    id: string;
    header: string;
    question: string;
    answer: string | null;
    qualifier: string;
}

/** A round is settled once no question is still pending. An absent/empty state map is not settled. */
export const isClarificationRoundSettled = (questionsState?: Record<string, ClarificationState>): boolean => {
    const states = Object.values(questionsState ?? {});
    return states.length > 0 && states.every((state) => state !== CLARIFICATION_STATE.PENDING);
};

/** Flips only the still-pending questions to `expired`; a settled round is returned untouched
 * (so a live resolution that already arrived is never overwritten). */
export const settleClarificationAsExpired = (card: ClarificationCard, cancelReason: ClarificationCancelReason): ClarificationCard => {
    // An optimistic settlement is not a real one: a stale-answer rollback must still expire it.
    if (isClarificationRoundSettled(card.questions_state) && !card.isOptimistic) return card;
    const questionsState = Object.fromEntries(
        Object.entries(card.questions_state).map(([id, state]) => [
            id,
            state === CLARIFICATION_STATE.PENDING || card.isOptimistic ? CLARIFICATION_STATE.EXPIRED : state,
        ]),
    );
    return {
        ...card,
        questions_state: questionsState,
        resolution: { mode: CLARIFICATION_RESOLUTION_MODE.CANCELLED, cancel_reason: cancelReason, questions: [] },
        isOptimistic: undefined,
    };
};

/** Settles the card from the answers the user just submitted, so the transcript can show the
 * Q&A note immediately; the server's `clarification_resolution` replaces it when it arrives. */
export const applyOptimisticClarificationAnswers = (card: ClarificationCard, answers: ClarificationAnswer[]): ClarificationCard => {
    if (isClarificationRoundSettled(card.questions_state)) return card;
    const questions: ClarificationResolutionQuestion[] = answers.flatMap((answer) => {
        const question = card.questions.find((candidate) => candidate.id === answer.question_id);
        if (!question) return [];
        const optionId = 'option_id' in answer ? answer.option_id : null;
        const recommended = question.options.find((option) => option.recommended);
        return [
            {
                question_id: question.id,
                header: question.header,
                question: question.question,
                options: question.options.map(({ id, label }) => ({ id, label })),
                recommended_option_id: recommended?.id ?? null,
                chosen_option_id: optionId,
                custom_text: 'text' in answer ? answer.text : null,
                source: optionId ? 'clicked' : 'typed',
                matched_recommended: !!optionId && optionId === recommended?.id,
                state: optionId ? CLARIFICATION_STATE.ANSWERED : CLARIFICATION_STATE.IN_CHAT,
            },
        ];
    });
    return {
        ...card,
        questions_state: { ...card.questions_state, ...Object.fromEntries(questions.map((record) => [record.question_id, record.state])) },
        resolution: { mode: CLARIFICATION_RESOLUTION_MODE.ANSWER, questions },
        isOptimistic: true,
    };
};

/** Folds a live `clarification_resolution` into the card it resolves. The server always wins over
 * an optimistic settlement. */
export const applyClarificationResolution = (card: ClarificationCard, resolution: ClarificationResolution): ClarificationCard => {
    // Idempotent: a repeated frame (or a replay) must not re-dispatch an already-settled card.
    if (isClarificationRoundSettled(card.questions_state) && !card.isOptimistic) return card;
    return {
        ...card,
        isOptimistic: undefined,
        questions_state: {
            ...card.questions_state,
            ...Object.fromEntries(resolution.questions.map((record) => [record.question_id, record.state])),
        },
        resolution: { mode: resolution.mode, cancel_reason: resolution.cancel_reason, questions: resolution.questions },
    };
};

/** Mirrors the server's rendered U2 summary ("Period: FY2025 YTD · Margin: operating margin"). */
export const buildClarificationAnswerSummary = (card: ClarificationCard, answers: ClarificationAnswer[]): string =>
    answers
        .map((answer) => {
            const question = card.questions.find((candidate) => candidate.id === answer.question_id);
            const text = 'text' in answer ? answer.text : question?.options.find((option) => option.id === answer.option_id)?.label ?? '';
            return `${question?.header ?? answer.question_id}: ${text}`;
        })
        .join(' · ');

const answerLabel = (record: ClarificationResolutionQuestion | undefined, fallbackOptions: Array<{ id: string; label: string }>) => {
    if (!record) return null;
    if (record.state === CLARIFICATION_STATE.IN_CHAT) return record.custom_text;
    const options = record.options?.length ? record.options : fallbackOptions;
    return options.find((option) => option.id === record.chosen_option_id)?.label ?? null;
};

/** Per-question note lines for the read-only transcript note, in the card's question order. */
export const formatClarificationNoteLines = (card: ClarificationCard): ClarificationNoteLine[] =>
    card.questions.map((question) => {
        const record = card.resolution?.questions.find((candidate) => candidate.question_id === question.id);
        const state = record?.state ?? card.questions_state[question.id] ?? CLARIFICATION_STATE.PENDING;
        const base = { id: question.id, header: question.header, question: question.question };

        if (state === CLARIFICATION_STATE.EXPIRED) {
            const reason = card.resolution?.cancel_reason;
            const why = reason ? `, ${CANCEL_REASON_COPY[reason]}` : '';
            return { ...base, answer: null, qualifier: `${CLARIFICATION_COPY.NOT_ANSWERED}${why}` };
        }
        if (state === CLARIFICATION_STATE.PENDING) {
            return { ...base, answer: null, qualifier: CLARIFICATION_COPY.AWAITING_ANSWER };
        }
        return {
            ...base,
            answer: answerLabel(record, question.options),
            qualifier: state === CLARIFICATION_STATE.IN_CHAT ? CLARIFICATION_COPY.YOU_TYPED : CLARIFICATION_COPY.YOU_PICKED,
        };
    });
